// src/PricingRowsEditor.js
// Edit a pricing table's rows — either for the agreement in front of you, or in
// the product template that agreement was built from.
//
// SCOPE is the thing to get right here. "This agreement only" edits the table
// the rep is looking at and nothing else; "The template" rewrites products.json
// and changes every future agreement built from that product. The two scopes
// edit different row lists (a dynamic table's measure rows exist only in the
// agreement), so switching reloads the draft rather than carrying edits across —
// an edit meant for one deal must never leak into the template.
//
// Billing shape is captured HERE, at authoring time, and written onto the row.
// That is what keeps orders invoiceable without a billing-spreadsheet round trip
// every time someone adds a line — and why the billing selector is not optional
// on a billable row.
import { useState } from "react";
import { billingFor } from "./shared/catalog";

const BILLING_OPTIONS = [
  { value: "one_time", label: "One-time" },
  { value: "annual", label: "Annual (recurring)" },
  { value: "monthly", label: "Monthly (recurring)" },
];

const blankRow = () => ({
  module: "", price: "", billing: "one_time",
  initial: true, optional: false, qty: false, isTotal: false, items: [],
});

// Existing rows predate row-level billing; show what the catalog says for them
// so saving backfills the classification rather than blanking it.
function withBilling(row) {
  if (row.isTotal) return { ...row, billing: null };
  if (row.billing) return row;
  const fromCatalog = billingFor(row.id);
  return { ...row, billing: fromCatalog === "unclassified" ? "" : fromCatalog };
}

const prepare = (rows) => (rows || []).map(withBilling);

// `sub` names which of the product's section buckets sectionIdx counts into —
// undefined for the main body, { type:'hosting' } or { type:'uds', category }
// for the inserts. See src/shared/sections.js.
export default function PricingRowsEditor({
  productId, productName, sectionIdx, sub, dualYear, yearLabels, label,
  rows, agreementRows, dynamic, hasOverride,
  onClose, onSaved, onApplyToAgreement, onResetAgreement,
}) {
  const years = dualYear ? (yearLabels && yearLabels.length === 2 ? yearLabels : ["Year 1", "Year 2"]) : null;
  const [scope, setScope] = useState("agreement");   // "agreement" | "template"
  const [draft, setDraft] = useState(() => prepare(agreementRows));
  const [tableLabel, setTableLabel] = useState(label || "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [problems, setProblems] = useState([]);

  const toAgreement = scope === "agreement";

  const switchScope = (next) => {
    if (next === scope) return;
    if (dirty && !window.confirm("Switch scope and discard the changes you just made here?")) return;
    setScope(next);
    setDraft(prepare(next === "agreement" ? agreementRows : rows));
    setTableLabel(label || "");
    setDirty(false);
    setError(null);
    setProblems([]);
  };

  const edit = (fn) => { setDirty(true); setDraft(fn); };
  const setRow = (i, patch) => edit(d => d.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => edit(d => d.filter((_, j) => j !== i));
  const addRow = () => edit(d => {
    const totalAt = d.findIndex(r => r.isTotal);          // keep the total row last
    const next = blankRow();
    if (totalAt === -1) return [...d, next];
    return [...d.slice(0, totalAt), next, ...d.slice(totalAt)];
  });
  const move = (i, dir) => edit(d => {
    const j = i + dir;
    if (j < 0 || j >= d.length) return d;
    const copy = [...d];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const missingBilling = draft.filter(r => !r.isTotal && !r.billing).length;

  const cleaned = () => draft.map(r => ({
    ...r,
    items: (r.items || []).filter(x => String(x).trim()),
    billing: r.isTotal ? undefined : (r.billing || undefined),
  }));

  const applyHere = () => onApplyToAgreement?.(cleaned(), tableLabel);

  const saveToTemplate = async () => {
    setSaving(true); setError(null); setProblems([]);
    try {
      const payload = cleaned();
      const res = await fetch(`/api/products/${encodeURIComponent(productId)}/sections/${sectionIdx}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows: payload, label: tableLabel, sub }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "Save failed");
        setProblems(data.problems || []);
        return;
      }
      onSaved?.(data.rows || payload, data.assigned || []);
    } catch (e) {
      setError("Could not reach the server. Your changes have not been saved.");
    } finally { setSaving(false); }
  };

  const cell = { padding: "6px 8px", verticalAlign: "top", borderBottom: "1px solid #eef0f6" };
  const th = { padding: "6px 8px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#8a90a6", letterSpacing: ".03em", borderBottom: "1px solid #e3e6ef" };
  const tab = (active) => ({
    padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
    border: `1px solid ${active ? "#1a3a6e" : "#d7dbe7"}`,
    background: active ? "#1a3a6e" : "#fff",
    color: active ? "#fff" : "#5b6178",
  });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
         onClick={() => !saving && onClose()}>
      <div style={{ background: "#fff", borderRadius: 12, width: 1040, maxWidth: "97vw", maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 40px rgba(0,0,0,0.25)" }}
           onClick={e => e.stopPropagation()}>

        <div style={{ padding: "18px 22px 12px", borderBottom: "1px solid #eef0f6" }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#1a3a6e" }}>Edit pricing table</div>

          <div style={{ marginTop: 10, display: "flex", alignItems: "center" }}>
            <button type="button" onClick={() => switchScope("agreement")}
                    style={{ ...tab(toAgreement), borderRadius: "6px 0 0 6px" }}>
              This agreement only
            </button>
            <button type="button" onClick={() => switchScope("template")}
                    style={{ ...tab(!toAgreement), borderRadius: "0 6px 6px 0", borderLeft: "none" }}>
              The template
            </button>
          </div>

          <div style={{ fontSize: 12, color: "#666", marginTop: 8, lineHeight: 1.5 }}>
            {toAgreement ? (
              <>Changes stay in <strong>this agreement</strong> — the <strong>{productName || productId}</strong> template is untouched.</>
            ) : (
              <>Editing the <strong>{productName || productId}</strong> template — changes apply to <strong>future</strong> agreements
                 built from this product. Agreements already sent keep what they were sent.</>
            )}
          </div>

          {dynamic && (
            <div style={{ fontSize: 11, marginTop: 8, padding: "7px 10px", borderRadius: 6,
                          background: "#fff8e1", border: "1px solid #fcd34d", color: "#795548", lineHeight: 1.5 }}>
              {toAgreement
                ? <>The measure rows below come from the <strong>Measures to Add</strong> list in the sidebar. Applying
                    here freezes this table for this agreement — after that, adding a measure in the sidebar
                    will not change it.</>
                : <>Measure rows are built per agreement from the <strong>Measures to Add</strong> list, so they are not
                    in the template — only fixed rows (the Grand Total) are. Switch to <strong>This agreement only</strong> to
                    edit the measure rows.</>}
            </div>
          )}

          {toAgreement && hasOverride && (
            <div style={{ fontSize: 11, marginTop: 8, display: "flex", alignItems: "center", gap: 8, color: "#5b6178" }}>
              <span>This table has already been hand-edited for this agreement.</span>
              <button type="button" onClick={() => onResetAgreement?.()}
                      style={{ background: "#fff", border: "1px solid #d7dbe7", borderRadius: 5, color: "#1a3a6e", cursor: "pointer", fontSize: 11, fontWeight: 600, padding: "3px 9px" }}>
                Reset to template
              </button>
            </div>
          )}

          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#8a90a6" }}>TABLE LABEL</label>
            <input className="input" value={tableLabel} onChange={e => { setDirty(true); setTableLabel(e.target.value); }}
                   placeholder="e.g. Software" style={{ flex: "0 1 320px" }} />
          </div>
        </div>

        <div style={{ overflow: "auto", padding: "8px 14px 4px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ ...th, width: 28 }}></th>
                <th style={th}>Module / description</th>
                <th style={{ ...th, width: 120, textAlign: "center" }}>{years ? years[0] : "Price"}</th>
                {years && <th style={{ ...th, width: 120, textAlign: "center" }}>{years[1]}</th>}
                <th style={{ ...th, width: 150 }}>Billing</th>
                <th style={{ ...th, width: 190 }}>Behavior</th>
                <th style={{ ...th, width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {draft.map((r, i) => (
                <tr key={i} style={{ background: r.isTotal ? "#f5f6ff" : "transparent" }}>
                  <td style={{ ...cell, textAlign: "center", color: "#c3c7d4" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <button type="button" title="Move up" onClick={() => move(i, -1)}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "#aab", fontSize: 11, padding: 0 }}>▲</button>
                      <button type="button" title="Move down" onClick={() => move(i, 1)}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "#aab", fontSize: 11, padding: 0 }}>▼</button>
                    </div>
                  </td>
                  <td style={cell}>
                    <input className="input" style={{ width: "100%", boxSizing: "border-box", fontWeight: r.isTotal ? 700 : 400 }}
                           value={r.module || ""} placeholder="e.g. Gaps in Care Dashboard"
                           onChange={e => setRow(i, { module: e.target.value })} />
                    <textarea className="input" rows={r.items?.length ? Math.min(4, r.items.length + 1) : 1}
                              style={{ width: "100%", boxSizing: "border-box", marginTop: 4, fontSize: 11, color: "#555" }}
                              placeholder="Sub-bullets, one per line (optional)"
                              value={(r.items || []).join("\n")}
                              onChange={e => setRow(i, { items: e.target.value.split("\n") })} />
                    {r.id && <div style={{ fontSize: 10, color: "#bbb", marginTop: 3 }}>id: {r.id}</div>}
                  </td>
                  {/* A dual-year total belongs to ONE year column — the other
                      stays empty, which is how the preview and the PDF decide
                      where to print it. */}
                  {(years ? ["price", "price2"] : ["price"]).map((key, col) => {
                    const totalYear = r.totalYear === 2 ? 2 : 1;
                    const off = r.isTotal && years && totalYear !== col + 1;
                    return (
                      <td key={key} style={cell}>
                        <input className="input" disabled={off}
                               style={{ width: "100%", boxSizing: "border-box", textAlign: "center",
                                        background: off ? "#f4f5f9" : undefined, fontWeight: r.isTotal ? 700 : 400 }}
                               value={(off ? "" : r[key]) || ""} placeholder={off ? "—" : "$1,500"}
                               onChange={e => setRow(i, { [key]: e.target.value })} />
                        <div style={{ fontSize: 10, color: "#aaa", marginTop: 3, textAlign: "center" }}>
                          {off ? "" : r.isTotal ? 'leave "$" to auto-total' : 'or "Included"'}
                        </div>
                      </td>
                    );
                  })}
                  <td style={cell}>
                    {r.isTotal ? <span style={{ fontSize: 11, color: "#aaa" }}>—</span> : (
                      <>
                        <select className="input" style={{ width: "100%" }} value={r.billing || ""}
                                onChange={e => setRow(i, { billing: e.target.value })}>
                          <option value="">Choose…</option>
                          {BILLING_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        {!r.billing && <div style={{ fontSize: 10, color: "#c0392b", marginTop: 3 }}>needed for invoicing</div>}
                      </>
                    )}
                  </td>
                  <td style={cell}>
                    {r.isTotal ? (
                      <div style={{ fontSize: 11, color: "#8a90a6", fontWeight: 700 }}>
                        TOTAL ROW
                        {years && (
                          <select className="input" style={{ width: "100%", marginTop: 4, fontWeight: 400 }}
                                  value={r.totalYear === 2 ? 2 : 1}
                                  onChange={e => {
                                    const y = Number(e.target.value);
                                    // Move the "$" marker to the column this total now prints in.
                                    setRow(i, y === 2
                                      ? { totalYear: 2, price2: r.price2 || r.price || "$", price: undefined }
                                      : { totalYear: undefined, price: r.price || r.price2 || "$", price2: undefined });
                                  }}>
                            <option value={1}>totals {years[0]}</option>
                            <option value={2}>totals {years[1]}</option>
                          </select>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 11, color: "#555" }}>
                        <label><input type="checkbox" checked={!!r.initial} onChange={e => setRow(i, { initial: e.target.checked })} /> Client initials it</label>
                        <label><input type="checkbox" checked={!!r.optional} onChange={e => setRow(i, { optional: e.target.checked })} /> Optional add-on</label>
                        <label><input type="checkbox" checked={!!r.qty} onChange={e => setRow(i, { qty: e.target.checked, pricing: e.target.checked ? (r.pricing || { unit: 0 }) : undefined })} /> Ask for a quantity</label>
                        {r.qty && (
                          <div style={{ marginTop: 4, paddingLeft: 6, borderLeft: "2px solid #e3e6ef", display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {["unit", "bundleSize", "bundlePrice", "unlimited"].map(k => (
                              <label key={k} style={{ fontSize: 10, color: "#8a90a6" }}>
                                {k}
                                <input className="input" style={{ width: 66, padding: "2px 4px", fontSize: 11 }} type="number"
                                       value={r.pricing?.[k] ?? ""}
                                       onChange={e => setRow(i, { pricing: { ...(r.pricing || {}), [k]: e.target.value === "" ? undefined : Number(e.target.value) } })} />
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={{ ...cell, textAlign: "center" }}>
                    <button type="button" title="Remove row" onClick={() => removeRow(i)}
                            style={{ background: "none", border: "none", color: "#c0392b", cursor: "pointer", fontSize: 14 }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button type="button" onClick={addRow}
                  style={{ marginTop: 10, background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 5, color: "#1a3a6e", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "6px 12px" }}>
            + Add row
          </button>
        </div>

        {(error || problems.length > 0 || missingBilling > 0) && (
          <div style={{ margin: "0 18px", padding: "10px 12px", background: error || problems.length ? "#fdecea" : "#fff8e1",
                        border: `1px solid ${error || problems.length ? "#f5c6cb" : "#fcd34d"}`, borderRadius: 6, fontSize: 12,
                        color: error || problems.length ? "#a3342a" : "#795548" }}>
            {error && <div style={{ fontWeight: 600, marginBottom: problems.length ? 6 : 0 }}>{error}</div>}
            {problems.map((p, i) => <div key={i}>• {p}</div>)}
            {!error && !problems.length && missingBilling > 0 && (
              <div>{missingBilling} row{missingBilling !== 1 ? "s" : ""} still need a billing type — without it those lines can’t be bucketed for invoicing.</div>
            )}
          </div>
        )}

        <div style={{ padding: "14px 22px", borderTop: "1px solid #eef0f6", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} disabled={saving}
                  style={{ padding: "9px 18px", borderRadius: 7, border: "1px solid #ddd", background: "#fff", color: "#555", fontSize: 13, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={toAgreement ? applyHere : saveToTemplate} disabled={saving}
                  style={{ padding: "9px 20px", borderRadius: 7, border: "none", background: "#1a3a6e", color: "#fff", fontSize: 13, fontWeight: 600, cursor: saving ? "wait" : "pointer", opacity: saving ? .6 : 1 }}>
            {saving ? "Saving…" : toAgreement ? "Apply to this agreement" : "Save to template"}
          </button>
        </div>
      </div>
    </div>
  );
}
